/**
 * Native Upload bridge for Capacitor native app.
 *
 * On native: delegates the entire multipart upload to Java threads
 * that are immune to WebView JS throttling when the app is backgrounded.
 *
 * On web: all calls are no-ops / return false.
 */

let NativeUpload = null;

function getPlugin() {
  if (NativeUpload) return NativeUpload;
  try {
    const { registerPlugin } = require('@capacitor/core');
    NativeUpload = registerPlugin('NativeUpload');
  } catch {
    NativeUpload = null;
  }
  return NativeUpload;
}

function isNative() {
  try {
    return window.Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

/**
 * Returns true if the native upload plugin is available (Android native app).
 */
export function isNativeUploadAvailable() {
  if (!isNative()) return false;
  return getPlugin() != null;
}

/**
 * Start a native upload. The entire multipart flow runs in Java.
 * @param {Object} params
 * @param {string[]} params.filePaths - Capacitor Filesystem paths
 * @param {string[]} params.fileNames - Original file names
 * @param {string[]} params.fileTypes - MIME types
 * @param {number[]} params.fileSizes - File sizes in bytes
 * @param {string} params.date - Session date (YYYY-MM-DD)
 * @param {string} params.token - JWT auth token
 * @param {string} params.apiBase - Fully resolved API base URL
 * @param {string} params.queueId - Queue item ID for progress tracking
 * @returns {Promise<{started: boolean, queueId: string}>}
 */
export async function startNativeUpload(params) {
  const plugin = getPlugin();
  if (!plugin) throw new Error('NativeUpload plugin not available');
  // autoCompress defaults to true; caller may pass false to skip (e.g. already-compressed files)
  return await plugin.startUpload({ autoCompress: true, ...params });
}

/**
 * Get current upload status from the native side.
 * @returns {Promise<{status: string, queueId?: string, progress?: number, sessionId?: string, error?: string}>}
 */
export async function getNativeUploadStatus() {
  const plugin = getPlugin();
  if (!plugin) return { status: 'idle' };
  return await plugin.getStatus();
}

/**
 * Cancel the in-progress native upload.
 */
export async function cancelNativeUpload() {
  const plugin = getPlugin();
  if (!plugin) return;
  return await plugin.cancelUpload();
}

/**
 * Add a listener for native upload events.
 * Events: 'nativeUploadProgress', 'nativeUploadComplete', 'nativeUploadFailed'
 * @param {string} eventName
 * @param {Function} callback
 * @returns {Promise<{remove: Function}>} - Call remove() to unsubscribe
 */
export async function addNativeUploadListener(eventName, callback) {
  const plugin = getPlugin();
  if (!plugin) return { remove: () => {} };
  return await plugin.addListener(eventName, callback);
}

/**
 * Copy a file to the app's data directory using native Java I/O.
 * Bypasses the Capacitor bridge's JSON serialization — zero JS memory usage.
 * Solves OOM crash for large files (>50MB) that would exceed the bridge's heap limit.
 *
 * @param {string} sourceUri - Content URI, file:// URI, or absolute path
 * @param {string} destPath - Relative path under app data dir (e.g. "upload-queue/file.mp3")
 * @returns {Promise<{path: string, size: number}>}
 */
export async function nativeCopyFile(sourceUri, destPath) {
  const plugin = getPlugin();
  if (!plugin) throw new Error('NativeUpload plugin not available');
  return await plugin.copyFileToStorage({ sourceUri, destPath });
}

/**
 * Delete a file from the app's data directory via native Java.
 * @param {string} path - Relative path under app data dir
 */
export async function nativeDeleteFile(path) {
  const plugin = getPlugin();
  if (!plugin) return;
  return await plugin.deleteFile({ path });
}

/**
 * Get info about a file in the app's data directory.
 * @param {string} path - Relative path under app data dir
 * @returns {Promise<{exists: boolean, size: number, absolutePath: string}>}
 */
export async function nativeGetFileInfo(path) {
  const plugin = getPlugin();
  if (!plugin) return { exists: false, size: 0, absolutePath: '' };
  return await plugin.getFileInfo({ path });
}
